import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// In-context resources for the block you're working on. Given the block's
// project, it surfaces that project's linked library resources + its ad-hoc
// project-only links — so the brief/template/tool is one click away on the
// active-block widget (Calendar) and in Focus mode, not buried on the
// project page. Renders nothing when there's no project or no resources.
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { safeHref } from "../lib/safeUrl";
export default function BlockResources({ projectId, variant = "panel", onLoaded, }) {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    // Hold the latest callback in a ref so the fetch effect can call it
    // without taking it as a dependency (avoids re-fetching on every render).
    const onLoadedRef = useRef(onLoaded);
    onLoadedRef.current = onLoaded;
    useEffect(() => {
        let cancelled = false;
        if (!projectId) {
            setItems([]);
            setLoaded(true);
            onLoadedRef.current?.(0);
            return;
        }
        setLoaded(false);
        (async () => {
            try {
                // Settle independently: if the ad-hoc /links endpoint isn't
                // deployed yet (404), library resources still render, and vice
                // versa — neither failure hides the other.
                const [resR, linkR] = await Promise.allSettled([
                    api.get(`/projects/${projectId}/resources`),
                    api.get(`/projects/${projectId}/links`),
                ]);
                if (cancelled)
                    return;
                const res = resR.status === "fulfilled" ? resR.value : [];
                const links = linkR.status === "fulfilled" ? linkR.value : [];
                const merged = [
                    ...res.map((r) => ({
                        id: `r-${r.id}`,
                        title: r.title,
                        url: r.url,
                        icon: r.icon || "🔗",
                        kind: r.category,
                    })),
                    ...links.map((l) => ({
                        id: `l-${l.id}`,
                        title: l.title,
                        url: l.url,
                        icon: "🔗",
                        kind: "Link",
                    })),
                ];
                setItems(merged);
                onLoadedRef.current?.(merged.length);
            }
            catch {
                /* non-fatal — the panel just stays hidden */
                if (!cancelled)
                    onLoadedRef.current?.(0);
            }
            finally {
                if (!cancelled)
                    setLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId]);
    // Hide entirely when there's nothing to show — keeps the widget clean.
    if (!projectId || !loaded || items.length === 0)
        return null;
    return (_jsxs("div", { className: `block-resources block-resources-${variant}`, children: [_jsx("div", { className: "block-resources-label", children: variant === "rail" ? "On hand" : "Resources for this block" }), _jsx("ul", { className: "block-resources-list", children: items.map((it) => (_jsxs("li", { className: "block-resources-row", children: [_jsx("span", { className: "block-resources-icon", "aria-hidden": "true", children: it.icon }), _jsxs("span", { className: "block-resources-text", children: [_jsx("a", { href: safeHref(it.url), target: "_blank", rel: "noopener noreferrer", className: "block-resources-title", children: it.title }), variant === "panel" && (_jsx("span", { className: "block-resources-kind muted small", children: it.kind }))] }), _jsx("a", { href: safeHref(it.url), target: "_blank", rel: "noopener noreferrer", className: "block-resources-open", "aria-label": `Open ${it.title}`, children: variant === "rail" ? "Open" : "Open ↗" })] }, it.id))) })] }));
}
